import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { createMask } from '@ngneat/input-mask';
import { assign } from 'lodash';
import { forkJoin, from } from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import { UserForm } from 'src/app/classes/form-groups/user-form';
import { Company, User, UserAccountRole } from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { AssignSitesToUserModalComponent } from 'src/app/modals/assign-sites-to-user-modal/assign-sites-to-user-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ImpersonationService } from 'src/app/services/impersonation.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import {
  SSO_USER_RESET_REFUSED_COPY,
  isSsoSignInRequired
} from 'src/app/services/vive-api-errors';
import { usersService } from 'src/app/shared/vive-api/users.service';
import { UsersPasswordEmailRequestDtoTypeEnum } from 'src/app/shared/vive-api/vive-api-models';

declare type ContactTypeLabel =
  'Bid Contact' | 'Billing Contact' | 'Compliance Contact';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UserComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private UserService: UserService,
    private AuthService: AuthService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private ImpersonationService: ImpersonationService,
    private CompanyService: CompanyService,
    private HostElement: ElementRef
  ) {
    this.UserFormGroup = new UserForm();
    const {
      controls: { Contact }
    } = this.UserFormGroup;
    Contact.removeControl('MailingAddress');

    // Remove the validators for these fields
    const ContactControlFields: (keyof ContactForm['controls'])[] = [
      'email',
      'firstName',
      'lastName',
      'phone1'
    ];
    ContactControlFields.forEach((field) => {
      Contact.controls[field]?.removeValidators(Validators.required);
      Contact.controls[field]?.updateValueAndValidity();
    });

    this.UserFormGroup.controls.UserAccountRoleId.valueChanges.subscribe({
      next: (id) => {
        const UserAccountRole = this.UserAccountRoles.find(
          (role) => role.id === id
        );
        if (UserAccountRole) {
          const { role } = UserAccountRole;
          const ClientRoles = [
            'CLIENT_ADMIN',
            'CLIENT_VENDOR_AND_SITES_MANAGER'
          ];

          this.showClientDropDown = !!role && new Set(ClientRoles).has(role);
        } else {
          this.showClientDropDown = false;
        }

        const CompanyIdControl = this.UserFormGroup.controls.CompanyId;

        if (this.showClientDropDown) {
          CompanyIdControl.addValidators([Validators.required]);
          CompanyIdControl.updateValueAndValidity();
        } else {
          CompanyIdControl.removeValidators([Validators.required]);
          CompanyIdControl.updateValueAndValidity();
        }
        this._cdr.markForCheck();
      }
    });
  }

  VendorId?: number;
  CompanyId?: number;
  User?: User;

  LoggedInUser!: User;
  LoggedInUserType!: 'Client' | 'Vendor' | 'Admin';
  SubNationalDivisions: SubNationalDivision[] = [];
  localizationInfo!: LocalizationInfo;
  country!: 'US' | 'CA';
  emailRegex = this.UtilityService.getRegex('email');
  UserAccountRoles: UserAccountRole[] = [];
  showClientDropDown: boolean = false;
  Companies: Company[] = [];

  SelectedContactTypeOptions: Set<ContactTypeLabel> = new Set();
  ContactTypeOptions: { label: ContactTypeLabel; value: boolean }[] = [];
  contactTypeDropdownOpen: boolean = false;

  AccountTypeToRouteMap: {
    Admin: 'admin';
    Vendor: 'vendor';
    Client: string;
  } = {
    Admin: 'admin',
    Vendor: 'vendor',
    Client: `client`
  };

  ContactTypeLabelToFieldMap: {
    'Bid Contact': 'isBid';
    'Billing Contact': 'isBilling';
    'Compliance Contact': 'isCompliance';
  } = {
    'Bid Contact': 'isBid',
    'Billing Contact': 'isBilling',
    'Compliance Contact': 'isCompliance'
  };

  ContactFieldToTypeLabelMap: {
    isBid: 'Bid Contact';
    isBilling: 'Billing Contact';
    isCompliance: 'Compliance Contact';
  } = {
    isBid: 'Bid Contact',
    isBilling: 'Billing Contact',
    isCompliance: 'Compliance Contact'
  };

  ErrorTypeToMessageMap: {
    PrimaryBidContactError: 'Please assign the bid contact type to another user before removing it from your profile.';
    PrimaryBillingContactError: 'Please assign the billing contact type to another user before removing it from your profile.';
    PrimaryComplianceContactError: 'Please assign the compliance contact type to another user before removing it from your profile.';
  } = {
    PrimaryBidContactError:
      'Please assign the bid contact type to another user before removing it from your profile.',
    PrimaryBillingContactError:
      'Please assign the billing contact type to another user before removing it from your profile.',
    PrimaryComplianceContactError:
      'Please assign the compliance contact type to another user before removing it from your profile.'
  };

  phoneMask = createMask('(999) 999-9999');
  phoneExtensionMask = createMask({ regex: '\\d{1,6}', placeholder: '' });
  usZipMask = createMask('99999');
  canadianZipMask = createMask('A9A 9A9');

  UserFormGroup: UserForm;

  isClientAdmin() {
    return !!this.UserAccountRoles.find((UserAccountRole) => {
      const { id, role } = UserAccountRole;
      return (
        id === this.UserFormGroup.controls['UserAccountRoleId'].value &&
        role === 'CLIENT_ADMIN'
      );
    });
  }

  /** Only VIVE Admin and Project Management (Implementation) may edit Allow Overrides. */
  canEditAllowOverrides(): boolean {
    const role = this.LoggedInUser?.UserAccountRole?.role;
    return (
      !!role &&
      new Set(['ADMIN_ADMIN', 'ADMIN_PROJECT_MANAGEMENT_TEAM']).has(role)
    );
  }

  private syncAllowOverridesEditability(): void {
    const controls = [
      this.UserFormGroup.controls.isSuperUser,
      this.UserFormGroup.controls.canWaive
    ];

    const editable = this.canEditAllowOverrides();

    for (const control of controls) {
      if (editable) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }
  }

  showImpersonateButton() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      if (!role) {
        throw new Error('Missing user permission.');
      }

      return new Set([
        'ADMIN_ADMIN',
        'ADMIN_INSURANCE_TEAM',
        'ADMIN_FRAUD_TEAM',
        'ADMIN_CUSTOMER_SERVICE_TEAM',
        'ADMIN_PROJECT_MANAGEMENT_TEAM'
      ]).has(role);
    }

    return false;
  }

  isLoggedInUserClientOrVendor() {
    return new Set(['Client', 'Vendor']).has(this.LoggedInUserType);
  }

  getUserAccountTypeLabel(role: UserAccountRoles) {
    if (
      this.LoggedInUser &&
      this.LoggedInUser.UserAccountRole &&
      new Set([
        'ADMIN_ADMIN',
        'ADMIN_PROJECT_MANAGEMENT_TEAM',
        'ADMIN_CUSTOMER_SERVICE_TEAM'
      ]).has(this.LoggedInUser.UserAccountRole.role!)
    ) {
      return {
        VENDOR_ADMIN: 'Vendor Admin',
        CLIENT_ADMIN: 'Client Admin',
        CLIENT_VENDOR_AND_SITES_MANAGER: 'Vendor & Sites Manager',
        ADMIN_ADMIN: 'VIVE Admin',
        ADMIN_PRIME_TEAM: 'VIVE PRIME Team',
        ADMIN_EMAILS_TEAM: 'VIVE Email Team',
        ADMIN_INSURANCE_TEAM: 'VIVE Insurance Team',
        ADMIN_FRAUD_TEAM: 'VIVE Fraud Team',
        ADMIN_CUSTOMER_SERVICE_TEAM: 'VIVE Customer Service Team',
        ADMIN_PROJECT_MANAGEMENT_TEAM: 'VIVE Project Management Team',
        EXTERNAL_VIVE_EMPLOYEE_LEVEL_1: 'External VIVE Employee Level 1',
        EXTERNAL_VIVE_EMPLOYEE_LEVEL_2: 'External VIVE Employee Level 2'
      }[role];
    }

    return {
      VENDOR_ADMIN: 'Admin',
      CLIENT_ADMIN: 'Admin',
      CLIENT_VENDOR_AND_SITES_MANAGER: 'Vendor & Sites Manager',
      ADMIN_ADMIN: 'Admin',
      ADMIN_PRIME_TEAM: 'VIVE Admin',
      ADMIN_EMAILS_TEAM: 'VIVE Admin',
      ADMIN_INSURANCE_TEAM: 'VIVE Admin',
      ADMIN_FRAUD_TEAM: 'VIVE Admin',
      ADMIN_CUSTOMER_SERVICE_TEAM: 'VIVE Admin',
      ADMIN_PROJECT_MANAGEMENT_TEAM: 'VIVE Admin',
      EXTERNAL_VIVE_EMPLOYEE_LEVEL_1: 'External VIVE Employee Level 1',
      EXTERNAL_VIVE_EMPLOYEE_LEVEL_2: 'External VIVE Employee Level 2'
    }[role];
  }

  deselectBidBillingCompliance() {
    this.UserFormGroup.controls['Contact'].patchValue({
      isBid: false,
      isBilling: false,
      isCompliance: false
    });
    this.SelectedContactTypeOptions.clear();
  }

  isBidBillingComplianceContact() {
    const {
      value: { isBid, isBilling, isCompliance }
    } = this.UserFormGroup.controls['Contact'];
    return isBid || isBilling || isCompliance;
  }

  updateContactTypeSelections() {
    const ContactFormGroup = this.UserFormGroup.controls['Contact'];

    const ContactTypeFields: ['isBid', 'isBilling', 'isCompliance'] = [
      'isBid',
      'isBilling',
      'isCompliance'
    ];

    ContactTypeFields.forEach((contactTypeField) => {
      ContactFormGroup.controls[contactTypeField].patchValue(false);
    });

    Array.from(this.SelectedContactTypeOptions).forEach((scto) => {
      ContactFormGroup.controls[
        this.ContactTypeLabelToFieldMap[scto]
      ].patchValue(true);
    });
  }

  deselectContactType(ContactTypeLabel: ContactTypeLabel) {
    this.SelectedContactTypeOptions.delete(ContactTypeLabel);
    this.updateContactTypeSelections();
  }

  selectContactType(ContactTypeLabel: ContactTypeLabel) {
    this.SelectedContactTypeOptions.add(ContactTypeLabel);
    this.updateContactTypeSelections();
  }

  toggleContactTypeDropdown() {
    this.contactTypeDropdownOpen = !this.contactTypeDropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    const target = event.target as HTMLElement;
    const dropdown = this.HostElement.nativeElement.querySelector(
      '#contact-type-dropdown'
    );

    if (dropdown && !dropdown.contains(target)) {
      this.contactTypeDropdownOpen = false;
    }
  }

  setIdControls() {
    if (!this.LoggedInUser || !this.LoggedInUserType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must log in to proceed.',
        { returnError: true }
      );
    }

    const { CompanyId, VendorId } = this.LoggedInUser;

    const ContactFormGroup = this.UserFormGroup.controls['Contact'];

    if (this.LoggedInUserType === 'Vendor' && VendorId) {
      if (!VendorId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
          returnError: true
        });
      }

      this.UserFormGroup.patchValue({ VendorId });
      ContactFormGroup.patchValue({ VendorId });
    }

    if (this.LoggedInUserType === 'Client') {
      if (!CompanyId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
          returnError: true
        });
      }

      this.CompanyId = CompanyId;

      this.UserFormGroup.patchValue({ CompanyId });
      ContactFormGroup.patchValue({ CompanyId });
    }

    if (this.LoggedInUserType === 'Admin') {
      const formCompanyId = this.UserFormGroup.controls.CompanyId.value;
      if (formCompanyId) {
        this.CompanyId = formCompanyId;
      }
    }
  }

  displayUserName(User: User) {
    const { firstName, lastName } = User;

    if (!firstName) {
      throw new Error('Missing first name.');
    }

    if (!lastName) {
      throw new Error('Missing last name.');
    }

    return `${firstName} ${lastName}`;
  }

  assignSites() {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot assign sites. No user found.',
        { returnError: true }
      );
    }

    const modalRef = this.ModalService.open(AssignSitesToUserModalComponent, {
      centered: true,
      size: 'xl'
    });
    Object.assign(modalRef.componentInstance, {
      UserId: this.User.id,
      CompanyId: this.CompanyId
    });

    modalRef.closed.subscribe({
      next: ({ created, deleted }) => {
        this.ToastService.addSuccessToast(
          'Success',
          `Successfully assigned sites! ${created} site${created.length > 1 ? 's' : ''} assigned. ${deleted} site${deleted.length > 1 ? 's' : ''} removed.`
        );
      }
    });
  }

  resetForm() {
    this.UserFormGroup.reset();
    this.setIdControls();
    this.SelectedContactTypeOptions.clear();
  }

  navigateToAnotherPage() {
    const { CompanyId, VendorId } = this.LoggedInUser;
    const routeParts: (string | number | null | undefined)[] = [
      this.AccountTypeToRouteMap[this.LoggedInUserType]
    ];

    if (CompanyId) {
      routeParts.push(CompanyId);
    }

    if (VendorId) {
      routeParts.push(VendorId);
    }

    // If the user is the Client manager, take them back to the dashboard.
    if (
      this.LoggedInUser.UserAccountRole?.role ===
      'CLIENT_VENDOR_AND_SITES_MANAGER'
    ) {
      routeParts.push('dashboard');
    } else {
      routeParts.push('admin', 'users');
    }

    this.router.navigate(routeParts);
  }

  cancel() {
    this.navigateToAnotherPage();
  }

  deleteUser() {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing User Information.',
        { returnError: true }
      );
    }

    const confirmModalInstance = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    assign(confirmModalInstance.componentInstance, {
      displayText: 'Are you sure you want to delete this User?'
    });

    confirmModalInstance.result.then((confirm) => {
      if (confirm) {
        const { Contact } = this.User!;
        if (Contact) {
          Object.assign(Contact, { isArchived: true });
        }

        this.UserService.saveUser(
          Object.assign(this.User!, { deleted: true })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully deleted User.'
            );
            this.cancel();
          },
          error: () => {
            Object.assign(this.User!, { deleted: false });
            const { Contact } = this.User!;
            if (Contact) {
              Object.assign(Contact, { isArchived: false });
            }
          }
        });
      }
    });
  }

  undeleteUser() {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing User Information.',
        { returnError: true }
      );
    }

    const { Contact } = this.User;
    if (Contact) {
      Object.assign(Contact, { isArchived: false });
    }

    this.UserService.saveUser(
      Object.assign(this.User, { deleted: false })
    ).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully un-deleted User.'
        );
      },
      error: () => {
        Object.assign(this.User!, { isArchived: true });
        const { Contact } = this.User!;
        if (Contact) {
          Object.assign(Contact, { isArchived: true });
        }
      }
    });
  }

  saveUser() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.UserFormGroup
    );
    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with one of the required fields below.',
        { returnError: true }
      );
    }

    const User = this.UserFormGroup.value;
    const { Contact } = User;

    if (!Contact) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Contact information.',
        { returnError: true }
      );
    }

    Object.assign(Contact, this.UserService.mapUserToContact(User));

    this.UserService.saveUser(
      User,
      {},
      { manuallyHandleError: true }
    ).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'User was successfully saved.'
        );
        this.navigateToAnotherPage();

        if (!this.User) {
          this.resetForm();
        } else {
          Object.assign(this.User, User);
        }
      },
      error: (error) => {
        const { error: innerError, message } = error;
        if (typeof innerError === 'object' && innerError !== null) {
          const { name } = innerError;
          if (name === 'SequelizeUniqueConstraintError') {
            throw this.ToastService.addErrorToast(
              'Error',
              'This email is already registered with VIVE.',
              { returnError: true }
            );
          }

          if (
            new Set([
              'PrimaryBidContactError',
              'PrimaryBillingContactError',
              'PrimaryComplianceContactError'
            ]).has(name)
          ) {
            throw this.ToastService.addErrorToast(
              'Error',
              this.ErrorTypeToMessageMap[
                name as
                  | 'PrimaryBidContactError'
                  | 'PrimaryBillingContactError'
                  | 'PrimaryComplianceContactError'
              ],
              { returnError: true }
            );
          }
        } else {
          throw this.ToastService.addErrorToast('Error', message, {
            returnError: true
          });
        }
      }
    });
  }

  checkIfIsBidBillingComplianceContact() {
    const Contact = this.UserFormGroup.controls['Contact'].value;
    const { isBid, isBilling, isCompliance } = Contact;

    return isBid || isBilling || isCompliance;
  }

  logInAsUser() {
    if (!this.User) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing User information.',
        { returnError: true }
      );
    }

    // Only client users can be logged in as: vendor impersonation is not
    // offered.
    const { id: UserId, CompanyId } = this.User;

    if (!UserId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing User information.',
        { returnError: true }
      );
    }

    if (!CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'User does not have a Client Id.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    Object.assign(componentInstance, {
      displayText:
        'Logging in as this user will be recorded. Are you sure you want to continue?'
    });
    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          this.ImpersonationService.start({ userId: UserId });
        }
      }
    });
  }

  permissionGroupChanged() {
    const {
      value: { UserAccountRoleId }
    } = this.UserFormGroup;

    if (UserAccountRoleId) {
      const FoundUserAccountRole = this.UserAccountRoles.find(
        (uar) => uar.id === UserAccountRoleId
      );

      if (!FoundUserAccountRole) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing user account role information.',
          { returnError: true }
        );
      }

      const { role } = FoundUserAccountRole;

      this.UserFormGroup.patchValue({
        sendWeeklyStatusUpdates: role === 'CLIENT_ADMIN',
        sendDailyStatusUpdates: role === 'CLIENT_ADMIN'
      });
    }
  }

  // The API refuses a welcome email to your own account.
  public canResendInvite(): boolean {
    return (
      this.User?.registrationStatus === 'unconfirmed' &&
      this.User.id !== this.LoggedInUser.id
    );
  }

  public async resendPasswordEmail(
    type: UsersPasswordEmailRequestDtoTypeEnum
  ): Promise<void> {
    // The buttons render only for a loaded user, which always has an id.
    const userId = this.User?.id;
    if (userId === undefined || userId === null) {
      throw new Error('The user on the screen has no id.');
    }

    try {
      await usersService.sendPasswordEmail({
        pathParams: { userId },
        data: { type }
      });
    } catch (error) {
      // The interceptor toasts every other failure and leaves this one here.
      // Anything else is rethrown so it is still reported as unhandled.
      if (isSsoSignInRequired(error)) {
        this.ToastService.addErrorToast('Error', SSO_USER_RESET_REFUSED_COPY);
        return;
      }
      throw error;
    }

    this.ToastService.addSuccessToast('Success', 'Successfully sent email.');
  }

  loadUserInfo(UserId: number) {
    this.LoggedInUser = this.AuthService.getUser();

    const { UserAccountType, Vendor, Company } = this.LoggedInUser;

    if (!UserAccountType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Logged In User does not have account type record.',
        { returnError: true }
      );
    }

    const { name } = UserAccountType;

    const AccountTypes: string[] = ['Admin', 'Vendor', 'Client'];

    if (!name || !AccountTypes.includes(name)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Logged In User has invalid account role.',
        { returnError: true }
      );
    }

    this.LoggedInUserType = name as 'Client' | 'Vendor' | 'Admin';
    this.syncAllowOverridesEditability();

    if (this.LoggedInUserType === 'Client') {
      // Requested to hide this field from client users
      this.UserFormGroup.controls.allowTexts.patchValue(false);
    }

    const country = (Vendor || Company || { country: 'US' }).country;

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Country information',
        { returnError: true }
      );
    }

    this.country = country;

    this.localizationInfo = this.UtilityService.getLocalizedValuesMap(country);

    if (Vendor) {
      this.ContactTypeOptions.push(
        { label: 'Bid Contact', value: true },
        { label: 'Billing Contact', value: true },
        { label: 'Compliance Contact', value: true }
      );
    }

    if (UserId) {
      this.UserService.getUserV2(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: UserId,
          deleted: {
            $or: [true, false]
          },
          Contact: {
            options: { required: false },
            StreetAddress: {
              options: { required: false }
            },
            isArchived: { $in: [true, false] }
          }
        })
      ).subscribe({
        next: (User) => {
          if (!User) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not load User information.',
              { returnError: true }
            );
          }

          this.User = User;
          this.UserFormGroup.patchValue(this.User);
          this.syncAllowOverridesEditability();

          const { Contact } = this.User;

          if (!Contact) {
            throw this.ToastService.addErrorToast(
              'Error',
              'User is missing Contact.',
              { returnError: true }
            );
          }

          const ContactTypeFields: ['isBid', 'isBilling', 'isCompliance'] = [
            'isBid',
            'isBilling',
            'isCompliance'
          ];

          ContactTypeFields.forEach((ctf) => {
            if (Contact[ctf]) {
              this.selectContactType(this.ContactFieldToTypeLabelMap[ctf]);
            }
          });
          this._cdr.markForCheck();
        }
      });
    }

    forkJoin([
      this.UserService.getUserAccountRoles(),
      this.SubNationalDivisionService.getSubNationalDivisions({
        country: this.country
      }),
      this.CompanyService.getCompanies(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: ['id', 'legalName']
        })
      )
    ]).subscribe({
      next: ([UserAccountRoles, SubNationalDivisions, Companies]) => {
        this.UserAccountRoles.push(...UserAccountRoles);
        this.SubNationalDivisions.push(...SubNationalDivisions);
        this.Companies.push(...Companies);

        this.setIdControls();
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const UserId = Number(ParamMap.get('UserId'));
        this.loadUserInfo(UserId);
        this._cdr.markForCheck();
      }
    });
  }
}
